import DateTimePicker, { CustomDateTimePickerProps } from '../../inputs/DateTimePicker';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { BaseFormikFieldProps } from './types/FormikFieldProps';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';

export const FormikDateTimePicker = <T extends Record<string, unknown>>({
  id,
  label,
  required,
  testId,
  ...rest
}: BaseFormikFieldProps<T> & CustomDateTimePickerProps & { required?: boolean; testId?: string }) => {
  const {
    [TID_ATTR]: restTestId,
    slotProps: restSlotProps,
    ...restWithoutTestId
  } = rest as Record<string, unknown> & {
    [TID_ATTR]?: string;
    slotProps?: { textField?: Record<string, unknown> };
  };
  const baseTestId = testId ?? restTestId;
  const { status, setFieldValue, handleBlur, errorText, hasError, fieldValue } = useFormikField<T>({ id });

  if (status?.loading) {
    return <FormikFieldSkeleton />;
  }

  // Ensure value is never undefined (fix controlled/uncontrolled warning)
  const controlledValue = fieldValue === undefined ? null : fieldValue;

  const existingTextField = (restSlotProps?.textField ?? {}) as Record<string, unknown>;
  const existingSlotProps = (existingTextField.slotProps ?? {}) as Record<string, Record<string, unknown>>;

  const textFieldSlotProps = baseTestId
    ? {
        slotProps: {
          ...((existingSlotProps as Record<string, unknown>) || {}),
          inputLabel: {
            ...((existingSlotProps.inputLabel as Record<string, unknown>) || {}),
            [TID_ATTR]: `${baseTestId}:label`
          },
          htmlInput: {
            ...((existingSlotProps.htmlInput as Record<string, unknown>) || {}),
            [TID_ATTR]: `${baseTestId}:input`
          },
          formHelperText: {
            ...((existingSlotProps.formHelperText as Record<string, unknown>) || {}),
            [TID_ATTR]: `${baseTestId}:helper-text`
          }
        } as Record<string, unknown>,
        inputProps: {
          ...((existingTextField.inputProps ?? {}) as Record<string, unknown>),
          [TID_ATTR]: `${baseTestId}:input`
        }
      }
    : {};

  const openPickerButtonSlotProps = baseTestId ? ({ [TID_ATTR]: `${baseTestId}:open-button` } as Record<string, string>) : undefined;

  const pickerPopupSlotProps = baseTestId
    ? {
        popper: { [TID_ATTR]: `${baseTestId}:popper` } as Record<string, string>,
        desktopPaper: { [TID_ATTR]: `${baseTestId}:desktop-paper` } as Record<string, string>,
        layout: { [TID_ATTR]: `${baseTestId}:layout` } as Record<string, string>,
        calendarHeader: { [TID_ATTR]: `${baseTestId}:calendar-header` } as Record<string, string>
      }
    : undefined;

  const displayLabel =
    required && label ? (
      <span>
        {label}{' '}
        <span aria-hidden="true" style={{ color: 'var(--mui-palette-error-main)' }}>
          *
        </span>
      </span>
    ) : (
      label || undefined
    );

  return (
    <DateTimePicker
      fullWidth
      id={id}
      name={id}
      label={displayLabel}
      value={controlledValue as Date | null}
      onChange={(date) => setFieldValue(id, date as Date | null)}
      onBlur={handleBlur}
      error={hasError}
      helperText={errorText}
      {...restWithoutTestId}
      slotProps={
        {
          ...restSlotProps,
          ...(openPickerButtonSlotProps && { openPickerButton: openPickerButtonSlotProps }),
          ...pickerPopupSlotProps,
          textField: {
            ...existingTextField,
            ...textFieldSlotProps
          } as unknown as CustomDateTimePickerProps['slotProps']
        } as unknown as CustomDateTimePickerProps['slotProps']
      }
    />
  );
};
